import type { VariantProps } from 'tailwind-variants';
import type { ThemeSlots } from '#layers/ui/types/tv';
import { tv } from 'tailwind-variants';

/**
 * Card-tile theme for `TileRadioGroup`. The selected state keys off `data-[state=checked]`, which
 * reka-ui's `RadioGroupItem` emits.
 */
export const tileGroupTheme = tv({
	slots: {
		root: 'grid gap-2',
		item: `
			group flex items-center gap-2 rounded-md border text-start transition-colors
			hover:not-disabled:not-data-[state=checked]:border-accented
			focus:outline-primary
			disabled:cursor-not-allowed disabled:opacity-50
			data-[state=checked]:border-primary data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary
			data-[state=checked]:disabled:border-primary/20 data-[state=checked]:disabled:bg-primary/5
		`,
		itemIcon: `
			flex shrink-0 items-center justify-center rounded-md bg-elevated transition-colors
			group-data-[state=checked]:bg-default
		`,
		itemTextWrapper: 'flex w-full flex-col overflow-hidden',
		itemIconSize: '',
		itemLabel: `
			truncate text-sm font-medium
			group-disabled:text-muted
			group-data-[state=checked]:group-disabled:text-primary/80
		`,
		itemDescription: `
			truncate text-xs text-dimmed
			group-data-[state=checked]:text-primary/80
			group-data-[state=checked]:group-disabled:text-primary/50
		`,
		overflowItem: '',
	},
	variants: {
		size: {
			xs: {
				item: 'p-1.5',
				itemLabel: 'text-xs',
				itemDescription: 'text-2xs',
				itemIcon: 'size-7 rounded-sm',
				itemIconSize: '16',
			},
			sm: {
				item: 'p-1.5',
				itemIcon: 'size-9 rounded-sm',
				itemIconSize: '20',
			},
			md: {
				item: 'p-2',
				itemIcon: 'size-11 rounded-md',
				itemIconSize: '24',
			},
			lg: {
				item: 'p-3',
				itemIcon: 'size-11 rounded-md',
				itemIconSize: '24',
				itemTextWrapper: 'gap-0.5',
			},
		},
		icons: {
			true: {},
			false: {},
		},
		disabled: {
			true: {
				root: 'cursor-not-allowed',
			},
		},
	},
	compoundVariants: [
		{
			size: ['xs', 'sm'],
			icons: false,
			class: {
				item: 'px-2',
			},
		},
		{
			size: 'md',
			icons: false,
			class: {
				item: 'px-3',
			},
		},
		{
			size: 'lg',
			icons: false,
			class: {
				item: 'px-4',
			},
		},
	],
	defaultVariants: {
		size: 'md',
	},
});

export type TileGroupTheme = ThemeSlots<typeof tileGroupTheme>;
export type TileGroupVariants = VariantProps<typeof tileGroupTheme>;
